import {
  Authentication,
  AuthenticationTestType,
  DuplicateEndpointData,
  DuplicateIntegrationData,
  EditPollingVisibilityType,
  GenerateJqQueryParams,
  GenerateJQQueryResponse,
  Integration,
  IntegrationBase,
  IntegrationEndpointCreateType,
  IntegrationEndpointDetail,
  IntegrationEndpointMinimal,
  IntegrationEndpointUpdateType,
  IntegrationEndpointVariable,
  IntegrationPollingType,
  IntegrationTestRequestType,
  IntegrationTravellerActionFile,
  IntegrationWebhook,
  IntegrationWebhookCreateRemoveType,
  IntegrationWebhookCreateRequestType,
  IntegrationWebhookCreateType,
  IntegrationWebhookLog,
  IntegrationWebhookMinimal,
  IntegrationWebhookUpdateType,
  ListSyncTestRequestType,
  OpenAPIBulkCreateParams,
  OpenAPIBulkCreateResult,
  OpenAPIParseResult,
  ReorderIntegrationData,
  ValidateJqQueryParams,
  ValidateJQQueryResponse,
} from '@components/integrations/integrations-types';

import { JourneyType } from 'api/types';
import { isInTemplateRoute } from 'ui/router';

import { request } from '../request';

export const loadIntegrations = async (queryString: string, isTested: boolean) => {
  const url = isTested ? 'v1/integrations/?is_tested=1/' : 'v1/integrations/';
  const response = await request<Integration[]>(`${url}${queryString}`);
  return response.data;
};

export const loadIntegration = async (shortId: string) => {
  const response = await request<Integration>(`v1/integrations/${shortId}/`);
  return response.data;
};

export const loadIntegrationEndpoint = async (shortId: string) => {
  const url = isInTemplateRoute()
    ? `v1/templates/endpoints/${shortId}/`
    : `v1/integrations/endpoint/${shortId}/`;
  const response = await request<IntegrationEndpointDetail>(url);
  return response.data;
};

export const deleteIntegrationEndpoint = async (shortId: string) => {
  const response = await request.delete(`v1/integrations/endpoint/${shortId}/`);
  return response.data;
};

export const createIntegration = async (data: IntegrationBase) => {
  const response = await request.postForm<Integration>('v1/integrations/', data);
  return response.data;
};

export const updateIntegration = async ({
  integrationId,
  data,
}: {
  integrationId: Integration['shortId'];
  data: Partial<IntegrationBase>;
}) => {
  const response = await request.patchForm(`v1/integrations/${integrationId}/`, data);
  return response.data;
};

export const deleteIntegration = async (integrationId: Integration['shortId']) => {
  const response = await request.delete(`v1/integrations/${integrationId}/`);
  return response.data;
};

export const loadIntegrationJourneys = async (shortId: string) => {
  const response = await request<JourneyType[]>(`v1/integrations/${shortId}/journeys/`);
  return response.data;
};

export const createIntegrationEndpoint = async (data: IntegrationEndpointCreateType) => {
  const response = await request.post(`v1/integrations/endpoint/`, data);
  return response.data;
};

export const updateIntegrationEndpoint = async (
  shortId: string,
  data: IntegrationEndpointUpdateType
) => {
  const response = await request.patch(`v1/integrations/endpoint/${shortId}/`, data);
  return response.data;
};

export const updateIntegrationEndpointExampleFiles = async (shortId: string, data: FormData) => {
  const response = await request.post(`v1/integrations/endpoint/${shortId}/files/`, data);
  return response.data;
};

export const updateIntegrationEndpointDefaultFiles = async (shortId: string, data: FormData) => {
  const response = await request.post(`v1/integrations/endpoint/${shortId}/default-files/`, data);
  return response.data;
};

export const updateIntegrationAuthentication = async (shortId: string, data: Authentication) => {
  const response = await request.patch(`v1/integrations/${shortId}/`, { authentication: data });
  return response.data;
};

export const integrationTestRequest = async (
  shortId: string,
  data: { variables: IntegrationEndpointVariable[] }
) => {
  const response = await request.post<IntegrationTestRequestType>(
    `v1/integrations/endpoint/${shortId}/test-request/`,
    data
  );
  return response.data;
};

export const authenticationTest = async (data: AuthenticationTestType) => {
  const response = await request.post('v1/integrations/authentication/test/', data);
  return response.data;
};

export const loadJourneyIntegrationEndpoints = async (shortId: string) => {
  const response = await request<IntegrationEndpointMinimal[]>(
    `v1/integrations/journey/${shortId}/endpoints/`
  );
  return response.data;
};

export const loadTemplateJourneyIntegrationEndpoints = async (shortId: string) => {
  const response = await request<IntegrationEndpointMinimal[]>(
    `v1/templates/journey/${shortId}/endpoints/`
  );
  return response.data;
};

export const loadTravellerJourneyIntegrationEndpoint = async (shortId: string) => {
  const response = await request<IntegrationEndpointMinimal[]>(
    `v1/integrations/traveller-journey/${shortId}/endpoints/`
  );
  return response.data;
};
export const loadTravellerActionIntegrationEndpoint = async (shortId: string) => {
  const response = await request<IntegrationEndpointMinimal>(
    `v1/integrations/traveller-action/${shortId}/endpoints/`
  );
  return response.data;
};

export const loadIntegrationWebhook = async (shortId: string) => {
  const url = isInTemplateRoute()
    ? `v1/templates/webhooks/${shortId}/`
    : `v1/integrations/webhook/${shortId}/`;
  const response = await request.get<IntegrationWebhook>(url);
  return response.data;
};

export const createIntegrationWebhook = async (data: IntegrationWebhookCreateType) => {
  const response = await request.post(`v1/integrations/webhook/`, data);
  return response.data;
};

export const updateIntegrationWebhook = async (
  shortId: string,
  data: IntegrationWebhookUpdateType
) => {
  const response = await request.patch(`v1/integrations/webhook/${shortId}/`, data);
  return response.data;
};

export const subscribeIntegrationWebhook = async (shortId: string, data: object) => {
  const response = await request.post<IntegrationWebhookCreateRequestType>(
    `v1/integrations/webhook/${shortId}/subscribe/`,
    data
  );
  return response.data;
};

export const resubscribeMarketplaceWebhook = async (shortId: string) => {
  const response = await request.post<{ success: boolean; error?: string }>(
    `v1/marketplace/webhook/${shortId}/resubscribe/`
  );
  return response.data;
};

export const unsubscribeIntegrationWebhook = async (
  shortId: string,
  data: IntegrationWebhookCreateRemoveType
) => {
  const response = await request.post<IntegrationWebhookCreateRequestType>(
    `v1/integrations/webhook/${shortId}/unsubscribe/`,
    {
      ...data,
      body: data.body ? JSON.parse(data.body) : undefined,
    }
  );
  return response.data;
};

export const deleteIntegrationWebhook = async (shortId: string) => {
  const response = await request.delete(`v1/integrations/webhook/${shortId}/`);
  return response.data;
};

export const loadJourneyIntegrationWebhooks = async (shortId: string) => {
  const response = await request<IntegrationWebhookMinimal[]>(
    `v1/integrations/journey/${shortId}/webhooks/`
  );
  return response.data;
};

export const loadTemplateJourneyIntegrationWebhooks = async (shortId: string) => {
  const response = await request<IntegrationWebhookMinimal[]>(
    `v1/templates/journey/${shortId}/webhooks/`
  );
  return response.data;
};

export const loadIntegrationWebhookLogs = async (shortId: string) => {
  const response = await request<IntegrationWebhookLog[]>(
    `v1/integrations/webhook/${shortId}/logs/`
  );
  return response.data;
};

export const loadIntegrationTravellerActionFile = async (shortId: string) => {
  const response = await request<IntegrationTravellerActionFile>(
    `v1/integrations/traveller-action/${shortId}/file/`
  );
  return response.data;
};

export const listSyncTestRequest = async (integrationShortId: string, data: { url: string }) => {
  const response = await request.post<ListSyncTestRequestType>(
    `v1/integrations/${integrationShortId}/test-request/`,
    data
  );
  return response.data;
};

export const duplicateIntegrationEndpoint = async (
  shortId: string,
  data: DuplicateEndpointData
) => {
  const response = await request.post(`v1/integrations/endpoint/${shortId}/duplicate/`, data);
  return response.data;
};

export const duplicateIntegrationWebhook = async (shortId: string, data: DuplicateEndpointData) => {
  const response = await request.post(`v1/integrations/webhook/${shortId}/duplicate/`, data);
  return response.data;
};

export const duplicateIntegration = async (shortId: string, data: DuplicateIntegrationData) => {
  const response = await request.post(`v1/integrations/${shortId}/duplicate/`, data);
  return response.data;
};

export const editPollingVisibility = async (data: EditPollingVisibilityType) => {
  const response = await request.post(`v1/integrations/polling-visibility/`, data);
  return response.data;
};

export const listPollingEndpoints = async (journeyId: string) => {
  const response = await request.get<IntegrationPollingType[]>(
    `v1/integrations/polling-visibility/?journey_id=${journeyId}`
  );

  return response.data;
};

export const validateJqQuery = async (data: ValidateJqQueryParams) => {
  const response = await request.post<ValidateJQQueryResponse>(
    'v1/integrations/validate-jq/',
    data
  );
  return response.data;
};

export const generateJqQuery = async (data: GenerateJqQueryParams) => {
  const response = await request.post<GenerateJQQueryResponse>(
    'v1/integrations/generate-jq/',
    data
  );
  return response.data;
};

export const parseOpenAPISpec = async (integrationShortId: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await request.post<OpenAPIParseResult>(
    `v1/integrations/${integrationShortId}/openapi/parse/`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

export const createEndpointsFromOpenAPI = async ({
  integrationShortId,
  endpoints,
  updateBaseUrl,
  baseUrl,
}: OpenAPIBulkCreateParams) => {
  const response = await request.post<OpenAPIBulkCreateResult>(
    `v1/integrations/${integrationShortId}/openapi/create/`,
    {
      endpoints,
      updateBaseUrl,
      baseUrl,
    }
  );
  return response.data;
};

export const reorderIntegration = async (
  integrationShortId: string,
  data: ReorderIntegrationData
) => {
  const response = await request.post(`v1/integrations/${integrationShortId}/reorder/`, data);
  return response.data;
};
